import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Loader from './components/Loader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import WelfareCategoriesPage from './pages/WelfareCategories';
import './App.css';
import { useUser } from './context/UserContext';

import Settings from './pages/Settings';
import Login from './pages/Login';
import CompleteProfile from './pages/CompleteProfile';
import Profile from './pages/Profile';
import Retraining from './pages/Retraining';
import RetrainingManagement from './pages/dashboard/RetrainingManagement';
import WelfareCategories from './pages/dashboard/WelfareCategories';
import UsersManagement from './pages/dashboard/UsersManagement';
import SliderManager from './pages/dashboard/SliderManager';
import Contact from './pages/Contact';
import Assistant from './pages/Assistant';
import ContactSettings from './pages/dashboard/ContactSettings';
import ContactMessages from './pages/dashboard/ContactMessages';
import ServicesList from './pages/ServicesList';
import WelfareServicesManagement from './pages/dashboard/WelfareServicesManagement';
import ServiceDetail from './pages/ServiceDetail';
import RatingsManagement from './pages/dashboard/RatingsManagement';
import { createTheme, ThemeProvider } from '@mui/material/styles';
 
import { InstallPrompt, IosInstallGuide } from './components/InstallPrompt';
import NotificationsAdmin from './pages/dashboard/NotificationsAdmin';
import BoardMembersManagement from './pages/dashboard/BoardMembersManagement';
import AuditLogs from './pages/dashboard/AuditLogs';
import PageViewReport from './pages/dashboard/PageViewReport';
import PodcastManagement from './pages/dashboard/PodcastManagement';
import Pages from './pages/dashboard/PageManagement';
import Deputys from './pages/dashboard/DeputyManagement';
import CountyAddressesManagement from './pages/dashboard/CountyAddressesManagement';


import BoardMembers from './pages/BoardMembers';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import NewsManagement from './pages/dashboard/NewsManagement';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import EventsManagement from './pages/dashboard/EventsManagement';
import Notifications from './pages/Notifications';
import { isPushSubscribed} from './utils/push';
 
import { confirm } from './utils/showMessage';
import api from './utils/api';
import { trackPageView } from './utils/analytics';

const normalizeLinkPath = (link) => {
  if (!link) return '';
  try {
    const url = new URL(link, window.location.origin);
    return `${url.pathname}${url.search}`;
  } catch (e) {
    if (link.startsWith('/')) return link;
    return `/${link}`;
  }
};

const NotificationRouteTracker = ({ isAuthenticated }) => {
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) return;
    let isMounted = true;

    const checkNotifications = async () => {
      try {
        const res = await api.get('/notifications', { params: { limit: 20 } });
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!isMounted || list.length === 0) return;

        const currentPath = `${location.pathname}${location.search}`;
        const matches = list.filter((item) => {
          const linkPath = normalizeLinkPath(item?.link);
          if (!linkPath) return false;
          return linkPath === currentPath || linkPath === location.pathname;
        });

        for (const item of matches) {
          const id = item?._id || item?.id;
          if (!id) continue;
          try {
            await api.post(`/notifications/${id}/seen`, { clicked: true });
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        // ignore
      }
    };

    checkNotifications();
    return () => { isMounted = false; };
  }, [isAuthenticated, location.pathname, location.search]);

  return null;
};

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location]);

  return null;
};


function App() {
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem('app-settings');
    return savedSettings ? JSON.parse(savedSettings) : {
      themeMode: 'light',
      colorScheme: 'theme-indigo',
      direction: 'rtl'
    };
  });

  const [isAppInstalled, setIsAppInstalled] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  });
  const [installPromptDismissed, setInstallPromptDismissed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('installPromptDismissed') === 'true';
  });
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isIosGuideVisible, setIsIosGuideVisible] = useState(false);
  const [isIosDevice, setIsIosDevice] = useState(false);
  const [isDesktopDevice, setIsDesktopDevice] = useState(false);

  const theme = React.useMemo(() => createTheme({
    typography: {
      fontFamily: 'IranYekan, Arial',
    },
    palette: {
      mode: settings.themeMode === 'dark' ? 'dark' : 'light',
    },
    direction: settings.direction,
  }), [settings.themeMode, settings.direction]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const updateSettings = (newSettings) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    localStorage.setItem('app-settings', JSON.stringify(updated));
  };

  const persistInstallPrompt = (value) => {
    setInstallPromptDismissed(value);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('installPromptDismissed', value ? 'true' : 'false');
    }
  };

  const persistWasInstalled = (value) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('wasInstalled', value ? 'true' : 'false');
    }
  };

  const handleDismissInstallPrompt = () => {
    setShowInstallPrompt(false);
    persistInstallPrompt(true);
  };

  const handleInstallClick = async () => {
    if (isIosDevice) {
      setIsIosGuideVisible(true);
      return;
    }

    if (!deferredInstallPrompt) {
      persistInstallPrompt(true);
      setShowInstallPrompt(false);
      return;
    }

    try {
      deferredInstallPrompt.prompt();
      const choiceResult = await deferredInstallPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        setIsAppInstalled(true);
      } else {
        persistInstallPrompt(true);
      }
    } catch (error) {
      persistInstallPrompt(true);
    } finally {
      setDeferredInstallPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  const handleIosGuideClose = () => {
    setIsIosGuideVisible(false);
  };


// ScrollToTop component to be used inside Router
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.pathname, location.search]);
  return null;
}

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Apply Theme Mode
    if (settings.themeMode === 'dark') {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }

    // Apply Color Scheme
    const colorSchemes = [
      'theme-indigo', 'theme-purple', 'theme-pink', 'theme-red',
      'theme-orange', 'theme-yellow', 'theme-green', 'theme-teal', 'theme-cyan'
    ];
    document.body.classList.remove(...colorSchemes);
    if (settings.colorScheme) {
      document.body.classList.add(settings.colorScheme);
    }

    // Apply Direction
    document.documentElement.dir = settings.direction;

    // Apply Menu State
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
      document.documentElement.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
      document.documentElement.classList.remove('menu-open');
    }
  }, [isMenuOpen, settings]);

  useEffect(() => {
    if (typeof navigator === 'undefined') {
      return;
    }
    setIsIosDevice(/iphone|ipad|ipod/i.test(navigator.userAgent));
    const isMobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
    setIsDesktopDevice(!isMobile);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const checkDisplayMode = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
      setIsAppInstalled(isStandalone);
      if (!isStandalone && window.localStorage.getItem('wasInstalled') === 'true') {
        persistInstallPrompt(false);
        persistWasInstalled(false);
      }
    };
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = (event) => {
      const isStandalone = event.matches || window.navigator.standalone === true;
      setIsAppInstalled(isStandalone);
      if (!isStandalone && window.localStorage.getItem('wasInstalled') === 'true') {
        persistInstallPrompt(false);
        persistWasInstalled(false);
      }
    };
    checkDisplayMode();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredInstallPrompt(event);
    };
    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setShowInstallPrompt(false);
      persistInstallPrompt(true);
      persistWasInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const isAuthenticated = !!localStorage.getItem('token');

  useEffect(() => {
    if (isAppInstalled || installPromptDismissed) {
      setShowInstallPrompt(false);
      return;
    }
    if (isIosDevice || deferredInstallPrompt || isDesktopDevice) {
      setShowInstallPrompt(true);
    } else {
      setShowInstallPrompt(false);
    }
  }, [isAppInstalled, installPromptDismissed, isIosDevice, deferredInstallPrompt, isDesktopDevice]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof window === 'undefined' || typeof Notification === 'undefined') return;

    const currentPath = window.location.pathname || '';
    const isOnSettings = currentPath.startsWith('/settings');

    const ensurePermission = async () => {
      if (Notification.permission === 'granted') return;
      if (Notification.permission === 'default') {
        try {
          await Notification.requestPermission();
        } catch (e) {
          console.warn('Notification permission request failed', e);
        }
        return;
      }

      if (Notification.permission === 'denied' && !isOnSettings) {
        const goToSettings = window.confirm('اجازه اعلان‌ها غیرفعال است. برای فعال‌سازی به تنظیمات برویم؟');
        if (goToSettings) {
          window.location.replace('/settings');
        }
      }
    };

    ensurePermission();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof window === 'undefined' || typeof Notification === 'undefined') return;

    const currentPath = window.location.pathname || '';
    const isOnSettings = currentPath.startsWith('/settings');

    const ensureSubscription = async () => {
      if (Notification.permission !== 'granted') return;
      try {
        const subscribed = await isPushSubscribed();
        if (!subscribed) {
          // Check snooze
          const snoozeKey = 'push-subscription-snooze';
          const snoozeUntil = Number(localStorage.getItem(snoozeKey) || 0);
          const now = Date.now();
          if (snoozeUntil && snoozeUntil > now) return;

          const goToSettings = await confirm('اتصال اعلان‌ها فعال نیست. برای فعال‌سازی به تنظیمات برویم؟',
            "بله", "فعلا خیر!"
          );
          if (goToSettings && !isOnSettings) {
            window.location.replace('/settings');
          } else {
            // Snooze for 24 hours
            localStorage.setItem(snoozeKey, String(now + 24 * 60 * 60 * 1000));
          }
        }
      } catch (e) {
        console.warn('Push subscription check failed', e);
      }
    };

    ensureSubscription();
  }, [isAuthenticated]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleOpenInstallPrompt = () => {
      if (isAppInstalled) return;
      persistInstallPrompt(false);
      setShowInstallPrompt(true);
    };
    window.addEventListener('open-install-prompt', handleOpenInstallPrompt);
    return () => {
      window.removeEventListener('open-install-prompt', handleOpenInstallPrompt);
    };
  }, [isAppInstalled]);
  const isAdminUser = user?.userRole === 'admin';
  const isSiteAdminUser = user?.userRole === 'site-admin';
  const canViewSiteContact = isAuthenticated && (isAdminUser || isSiteAdminUser);
  const canViewAppContact = isAuthenticated && isAdminUser;
  const mainStyle = {
     paddingBottom: 80,
    ...(isAdminUser || isSiteAdminUser ? {} : { maxWidth: 601, margin: '0 auto' }),
  };

  return (
    <ThemeProvider theme={theme}>
      {/* <OfflineStatus /> */}
      <Router>
        <ScrollToTop />
        <NotificationRouteTracker isAuthenticated={isAuthenticated} />
        <AnalyticsTracker />
        {isLoading && <Loader />}
        {isAuthenticated && <Sidebar isOpen={isMenuOpen} toggleMenu={toggleMenu} />}
        <InstallPrompt
          visible={showInstallPrompt && !isAppInstalled}
          isIos={isIosDevice}
          hasPrompt={!!deferredInstallPrompt}
          onInstall={handleInstallClick}
          onClose={handleDismissInstallPrompt}
        />
        <main className="h-100"
          style={mainStyle}
        >
          <Header toggleMenu={toggleMenu} />
          <Routes >
            {/* ...existing routes... */}
            <Route path="/login" element={<Login />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route
              path="/"
              element={isAuthenticated ? <Home /> : <Navigate to="/login" />}
            />

            <Route
              path="/welfare-categories"
              element={isAuthenticated ? <WelfareCategoriesPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/settings"
              element={isAuthenticated ? <Settings settings={settings} updateSettings={updateSettings} /> : <Navigate to="/login" />}
            />
            <Route
              path="/profile"
              element={isAuthenticated ? <Profile /> : <Navigate to="/login" />}
            />
            <Route
              path="/retraining"
              element={isAuthenticated ? <Retraining /> : <Navigate to="/login" />}
            />
            <Route
              path="/contact"
              element={isAuthenticated ? <Contact /> : <Navigate to="/login" />}
            />
            <Route
              path="/assistant"
              element={isAuthenticated ? <Assistant /> : <Navigate to="/login" />}
            />
            <Route
              path="/notifications"
              element={isAuthenticated ? <Notifications /> : <Navigate to="/login" />}
            />
            <Route
              path="/board-members"
              element={isAuthenticated ? <BoardMembers /> : <Navigate to="/login" />}
            />
            <Route
              path="/dashboard/retraining"
              element={isAuthenticated && isAdminUser ? <RetrainingManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/welfare-categories"
              element={isAuthenticated && isAdminUser ? <WelfareCategories /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/users-management"
              element={isAuthenticated && isAdminUser ? <UsersManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/slider"
              element={isAuthenticated && isAdminUser ? <SliderManager /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/contact-settings"
              element={isAuthenticated && isAdminUser ? <ContactSettings /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/contact-messages"
              element={<Navigate to="/dashboard/contact-messages/site" replace />}
            />
            <Route
              path="/dashboard/contact-messages/site"
              element={isAuthenticated && isSiteAdminUser ? <ContactMessages source="web" /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/contact-messages/app"
              element={canViewAppContact ? <ContactMessages source="app" /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/notifications"
              element={isAuthenticated && isAdminUser ? <NotificationsAdmin /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/audit-logs"
              element={isAuthenticated && isAdminUser ? <AuditLogs /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/board-members"
              element={isAuthenticated && isAdminUser ? <BoardMembersManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/welfare-services"
              element={isAuthenticated && isAdminUser ? <WelfareServicesManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/ratings"
              element={isAuthenticated && isAdminUser ? <RatingsManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/news"
              element={isAuthenticated && isAdminUser ? <NewsManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/events"
              element={isAuthenticated && isAdminUser ? <EventsManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/page-view-report"
              element={isAuthenticated && isAdminUser ? <PageViewReport /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/pages"
              element={isAuthenticated && isSiteAdminUser ? <Pages /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/deputys"
              element={isAuthenticated && isSiteAdminUser ? <Deputys /> : <Navigate to="/" />}
            />
            <Route
              path="/dashboard/county-addresses"
              element={isAuthenticated && isSiteAdminUser ? <CountyAddressesManagement /> : <Navigate to="/" />}
            />

            <Route
              path="/dashboard/podcasts"
              element={isAuthenticated && isSiteAdminUser ? <PodcastManagement /> : <Navigate to="/" />}
            />

            
            <Route
              path="/news"
              element={isAuthenticated ? <News /> : <Navigate to="/login" />}
            />


            <Route
              path="/newsdetail/:id"
              element={isAuthenticated ? <NewsDetail /> : <Navigate to="/login" />}
            />
            <Route
              path="/events"
              element={isAuthenticated ? <Events /> : <Navigate to="/login" />}
            />
            <Route
              path="/eventdetail/:id"
              element={isAuthenticated ? <EventDetail /> : <Navigate to="/login" />}
            />
            <Route
              path="/welfare/:id"
              element={isAuthenticated ? <ServicesList /> : <Navigate to="/login" />}
            />
            <Route
              path="/welfare/service/:id"
              element={isAuthenticated ? <ServiceDetail /> : <Navigate to="/login" />}
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
        <IosInstallGuide visible={isIosGuideVisible} onClose={handleIosGuideClose} />
        {isAuthenticated && <Footer />}
      </Router>
    </ThemeProvider>
  );
}

export default App;
